import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
  verifyBeforeUpdateEmail,
  sendEmailVerification,
  signInWithCustomToken,
  type User,
} from 'firebase/auth'
import { getActivePinia } from 'pinia'
import { ref, type Ref } from 'vue'
import { analytics } from '~/services/analytics'
import { resetHelpCenterIdentity } from '~/services/helpCenterIdentity'
import { navigateTo } from 'nuxt/app'
import { usePostHog } from '~/composables/usePostHog'
import {
  clearMfaStatusCache,
  clearPendingMfaResolver,
  getMfaStatus,
  prepareEmailMfaChallenge,
  fetchRememberDevicePolicyForEmail,
  restoreTrustedDeviceForEmail,
  routeByMfaStatus,
  setPendingMfaResolverFromError,
} from '~/composables/useMfa'
import { NuxtRoutes } from '~/constants/routes'
import {
  errorMessage,
  getErrorCode,
  isExpectedNetworkError,
  isFirebaseSessionExpiredError,
  isInvalidLoginCredentialError,
} from '~/utils/helper'
import { normalizeEmailInput } from '~/utils/normalizeInputs'
// import { signOutUser } from "~/composables/useFirebase";

const firebaseUser = ref<User | null>(null) as Ref<User | null>
const isUserLoaded = ref(false)
let cachedToken: string | null = null
let signOutTimer: ReturnType<typeof setTimeout> | null = null
let inactivityTimerPaused = false
const impersonateStoreId = 'impersonate'
const impersonationStateKeys = [
  'impersonationSessionId',
  'impersonationExpiresAt',
  'effectiveCustomerId',
  'effectiveCustomerName',
  'impersonatedCustomerName',
  'impersonatedCustomerPlan',
  'impersonatedTeamWorkflowSettings',
] as const

type ImpersonationStateKey = typeof impersonationStateKeys[number]
type ImpersonationStateSnapshot = Partial<Record<ImpersonationStateKey, unknown>>

export const clearLocalImpersonationSession = () => {
  try {
    if (import.meta.client) {
      localStorage.removeItem(impersonateStoreId)
    }
  }
  catch {
    // Storage can be unavailable in tests or restricted browser contexts.
  }

  const impersonationState = getActivePinia()?.state.value[impersonateStoreId] as
    | ImpersonationStateSnapshot
    | undefined

  if (!impersonationState) {
    return
  }

  for (const key of impersonationStateKeys) {
    if (key in impersonationState) {
      impersonationState[key] = null
    }
  }
}

export type ResendVerificationResult
  = | { status: 'sent' }
    | { status: 'already-verified' }
    | { status: 'rate-limited' }
    | { status: 'invalid-credentials' }
    | { status: 'failed', code?: string }

const resetSignOutTimer = () => {
  if (signOutTimer) {
    clearTimeout(signOutTimer)
  }
  signOutTimer = setTimeout(
    () => {
      const auth = getAuth()
      auth.signOut() // sign out from firebase
      clearLocalImpersonationSession()
      firebaseUser.value = null // clear User Info
      navigateTo({ name: NuxtRoutes.AuthLogin })
      $log('User signed out due to inactivity')
    },
    60 * 60 * 1000,
  ) // 60 minutes
}

export const createUser = async (email: string, password: string) => {
  const auth = getAuth()
  const normalizedEmail = normalizeEmailInput(email)
  const credentials = await createUserWithEmailAndPassword(
    auth,
    normalizedEmail,
    password,
  ).catch((error: unknown) => {
    throw new Error(errorMessage(error))
  })
  return credentials
}

/** Transient Identity Toolkit failures that succeed on a single retry. */
const isTransientSignInError = (error: unknown): boolean => {
  const code = getErrorCode(error)
  return code === 'auth/missing-project-id' || isExpectedNetworkError(error)
}

type LoginFailureReason
  = | 'invalid_credentials'
    | 'too_many_requests'
    | 'email_not_verified'
    | 'email_mfa_prepare_failed'
    | 'unexpected_error'

const captureLoginAttempted = (email: string) => {
  usePostHog().capture('login_attempted', { email })
}

const captureLoginFailed = (
  email: string,
  reason: LoginFailureReason,
  error?: unknown,
) => {
  const errorCode = getErrorCode(error)
  usePostHog().capture('login_failed', {
    email,
    reason,
    ...(errorCode ? { error_code: errorCode } : {}),
  })
}

export const signInUser = async (email: string, password: string) => {
  clearLocalImpersonationSession()
  const auth = getAuth()
  const normalizedEmail = normalizeEmailInput(email)
  captureLoginAttempted(normalizedEmail)
  let credentials

  try {
    try {
      credentials = await signInWithEmailAndPassword(auth, normalizedEmail, password)
    }
    catch (error: unknown) {
      // Concurrent login-page session cleanup (or a brief Identity Toolkit blip) can
      // surface auth/missing-project-id even when projectId is correctly configured.
      if (!isTransientSignInError(error)) throw error
      $log('Transient sign-in error, retrying once:', getErrorCode(error) ?? error)
      credentials = await signInWithEmailAndPassword(auth, normalizedEmail, password)
    }
  }
  catch (error: unknown) {
    if (getErrorCode(error) === 'auth/multi-factor-auth-required') {
      let rememberDays = 0
      try {
        const restoreResult = await restoreTrustedDeviceForEmail(normalizedEmail)
        rememberDays = restoreResult.rememberDeviceDays
        if (restoreResult.restored && restoreResult.customToken) {
          credentials = await signInWithCustomToken(auth, restoreResult.customToken)
          if (credentials.user.emailVerified) {
            clearLocalImpersonationSession()
            firebaseUser.value = auth.currentUser
            $log('User signed in via trusted device')
            usePostHog().capture('user_logged_in', { login_method: 'trusted_device' })
            await routeByMfaStatus('/home')
            return credentials
          }
          await auth.signOut()
          firebaseUser.value = null
          captureLoginFailed(normalizedEmail, 'email_not_verified')
          return null
        }
      }
      catch (restoreError) {
        $reportError('Trusted device restore failed, falling back to MFA challenge:', restoreError)
        try {
          rememberDays = await fetchRememberDevicePolicyForEmail(normalizedEmail)
        }
        catch (policyError) {
          $reportError('Could not fetch remember device policy:', policyError)
        }
      }

      setPendingMfaResolverFromError(error, rememberDays)
      try {
        const emailChallenge = await prepareEmailMfaChallenge(normalizedEmail, password)
        if (emailChallenge) {
          setPendingMfaResolverFromError(error, emailChallenge.rememberDeviceDays)
        }
      }
      catch (prepareError) {
        $reportError('Email MFA preparation failed; TOTP remains available:', prepareError)
      }
      await navigateTo({ name: NuxtRoutes.AuthMfaChallenge })
      return 'mfa-required'
    }

    if (getErrorCode(error) === 'auth/too-many-requests') {
      captureLoginFailed(normalizedEmail, 'too_many_requests', error)
      return 'too-many-requests'
    }
    if (isInvalidLoginCredentialError(error)) {
      $log('Sign-in rejected: invalid credentials')
      captureLoginFailed(normalizedEmail, 'invalid_credentials', error)
      return 'invalid-credentials'
    }
    $reportError('Unexpected sign-in error:', error)
    captureLoginFailed(normalizedEmail, 'unexpected_error', error)
    throw new Error(errorMessage(error), { cause: error })
  }

  if (!credentials) return null
  if (!credentials.user.emailVerified) {
    await auth.signOut()
    firebaseUser.value = null
    captureLoginFailed(normalizedEmail, 'email_not_verified')
    return null
  }

  clearLocalImpersonationSession()
  firebaseUser.value = auth.currentUser
  const status = await getMfaStatus({ force: true })

  if (status?.verificationRequired) {
    try {
      const restoreResult = await restoreTrustedDeviceForEmail(normalizedEmail)
      if (restoreResult.restored && restoreResult.customToken) {
        credentials = await signInWithCustomToken(auth, restoreResult.customToken)
        firebaseUser.value = auth.currentUser
        usePostHog().capture('user_logged_in', { login_method: 'trusted_device' })
        await routeByMfaStatus('/home')
        return credentials
      }
    }
    catch (restoreError) {
      $reportError('Trusted device restore failed before email MFA:', restoreError)
    }

    const emailChallenge = await prepareEmailMfaChallenge(normalizedEmail, password)
    if (!emailChallenge) {
      await auth.signOut()
      firebaseUser.value = null
      captureLoginFailed(normalizedEmail, 'email_mfa_prepare_failed')
      throw new Error('Email MFA is required but could not be prepared')
    }
    await navigateTo({ name: NuxtRoutes.AuthMfaChallenge })
    return 'mfa-required'
  }

  $log('User signed in')
  usePostHog().capture('user_logged_in', { login_method: 'password' })
  await routeByMfaStatus('/home')
  return credentials
}

/** Unsubscribe handle for the single Firebase auth-state subscription. */
let authUnsubscribe: (() => void) | null = null
/** Shared readiness promise so concurrent/repeated initUser calls do not re-subscribe. */
let authInitPromise: Promise<boolean> | null = null

const applyAuthState = async (user: User | null) => {
  if (user) {
    try {
      // Always refresh — signInWithCustomToken (email MFA / trusted device)
      // replaces a password-only session and must not keep the old JWT cached.
      cachedToken = await user.getIdToken()
    }
    catch (error) {
      cachedToken = null
      $reportError('Error fetching token', error)
    }
    firebaseUser.value = user
    $log('User state changed')

    if (import.meta.client) {
      setupInactivityTimer()
    }
    return
  }

  firebaseUser.value = null
  cachedToken = null
  $log('User state changed: null')

  if (import.meta.client) {
    cleanupInactivityTimer()
  }
}

/**
 * Wires the canonical Firebase auth-state listener exactly once per app lifetime.
 * Safe to call from the Firebase plugin and from multiple `useFirebase()` consumers.
 *
 * The returned promise settles on the *first* auth-state delivery (signed-in user or
 * `null`). Later login/logout events keep updating shared refs through the same
 * listener — `authUnsubscribe` is not called on ordinary logout.
 */
export const initUser = (): Promise<boolean> => {
  if (authInitPromise) {
    return authInitPromise
  }

  authInitPromise = new Promise((resolve, reject) => {
    let settled = false

    const settleResolve = () => {
      if (settled) return
      settled = true
      resolve(true)
    }

    const settleReject = (error: unknown) => {
      if (settled) return
      settled = true
      reject(error)
    }

    try {
      const auth = getAuth()
      authUnsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          try {
            await applyAuthState(user)
            isUserLoaded.value = true
            settleResolve()
          }
          catch (error) {
            isUserLoaded.value = true
            settleReject(error)
          }
        },
        (error) => {
          isUserLoaded.value = true
          settleReject(error)
        },
      )
    }
    catch (error) {
      isUserLoaded.value = true
      settleReject(error)
    }
  })

  return authInitPromise
}

/**
 * Tears down the lifetime auth listener. Intended for tests and HMR cleanup only —
 * never call this during ordinary logout; later login events must reuse the same
 * subscription.
 */
export const resetFirebaseAuthSubscriptionForTests = () => {
  authUnsubscribe?.()
  authUnsubscribe = null
  authInitPromise = null
  firebaseUser.value = null
  isUserLoaded.value = false
  cachedToken = null
}

// Define these functions outside but only use them in client context
const setupInactivityTimer = () => {
  if (inactivityTimerPaused) {
    return
  }

  // Clean up first to avoid duplicate listeners
  cleanupInactivityTimer()

  // Then set up new listeners
  document.addEventListener('mousemove', resetSignOutTimer)
  document.addEventListener('keypress', resetSignOutTimer)
  resetSignOutTimer()
}

export const pauseInactivityTimer = () => {
  inactivityTimerPaused = true
  cleanupInactivityTimer()
}

export const resumeInactivityTimer = () => {
  inactivityTimerPaused = false
  if (firebaseUser.value && import.meta.client) {
    setupInactivityTimer()
  }
}

const cleanupInactivityTimer = () => {
  document.removeEventListener('mousemove', resetSignOutTimer)
  document.removeEventListener('keypress', resetSignOutTimer)
  if (signOutTimer) {
    clearTimeout(signOutTimer)
  }
  signOutTimer = null
}

export const clearCachedAuthToken = () => {
  cachedToken = null
}

/** Align the module token cache with the current Firebase session JWT. */
export const syncCachedAuthToken = async (user?: User | null): Promise<string | null> => {
  const current = user ?? getAuth().currentUser
  if (!current) {
    cachedToken = null
    return null
  }
  cachedToken = await current.getIdToken()
  return cachedToken
}

/**
 * Exchange an email-MFA custom token for a Firebase session that carries
 * `qutee_mfa_method=email`. Clears any prior password-only session first so
 * API calls cannot keep sending the pre-MFA JWT (which yields 403 on
 * /mfa/trusted-device/remember).
 */
export const signInWithEmailMfaToken = async (customToken: string) => {
  const auth = getAuth()
  if (auth.currentUser) {
    await auth.signOut()
    cachedToken = null
    firebaseUser.value = null
  }

  const credential = await signInWithCustomToken(auth, customToken)
  firebaseUser.value = credential.user

  // Use the exchanged ID token (do not force-refresh first) so developer
  // claims from create_custom_token are definitely present for this request.
  const tokenResult = await credential.user.getIdTokenResult()
  cachedToken = tokenResult.token

  if (tokenResult.claims.qutee_mfa_method !== 'email') {
    throw new Error('Email MFA sign-in did not produce the expected MFA claims')
  }

  return credential
}

export const getToken = async () => {
  const auth = getAuth()
  if (!auth.currentUser) {
    $log('No current user')
    return null
  }
  try {
    const currentUser = auth.currentUser
    const currentTime = Math.floor(Date.now() / 1000)
    const tokenResult = await currentUser.getIdTokenResult()
    if (auth.currentUser !== currentUser) {
      $log('User changed during token fetch')
      return null
    }
    const tokenExpiry = Math.floor(Date.parse(tokenResult.expirationTime) / 1000)

    if (currentTime >= tokenExpiry - 60) {
      cachedToken = await currentUser.getIdToken(true)
    }
    else {
      // Prefer the live session token over a module cache that can outlive
      // signInWithCustomToken (password JWT still present after email MFA).
      cachedToken = tokenResult.token
    }
    return cachedToken
  }
  catch (error) {
    if (isExpectedNetworkError(error) || isFirebaseSessionExpiredError(error)) {
      $log('Token refresh failed, session may have expired', error)
      cachedToken = null
      return null
    }
    throw error
  }
}

export const signOutUser = async () => {
  clearPendingMfaResolver()
  clearMfaStatusCache()
  clearLocalImpersonationSession()
  const auth = getAuth()
  await auth.signOut()
  firebaseUser.value = null
  cachedToken = null
  resetHelpCenterIdentity()
  analytics.reset() // Reset PostHog to clear user data
  $log('User signed out')
  // Prefer named route over `/auth/login` path — hosting redirects to a trailing
  // slash, and path navigation from the login page remounts and re-runs cleanup.
  navigateTo({ name: NuxtRoutes.AuthLogin })
}

/**
 * Clear any restored Firebase session when landing on the login page.
 *
 * Unlike `signOutUser`, this does not navigate (already on login) and waits for
 * auth persistence to finish hydrating first. Calling `signOutUser` from login
 * `onMounted` races with form submit / password-manager autofill and can surface
 * transient Firebase errors such as `auth/missing-project-id`.
 */
export const prepareLoginPageSession = async () => {
  clearPendingMfaResolver()
  clearMfaStatusCache()
  clearLocalImpersonationSession()

  try {
    await initUser()
  }
  catch (error) {
    $log('Auth init during login page cleanup failed:', error)
  }

  try {
    const auth = getAuth()
    if (auth.currentUser) {
      await auth.signOut()
    }
  }
  catch (error) {
    $log('Firebase sign-out during login page cleanup failed:', error)
  }

  firebaseUser.value = null
  cachedToken = null
  resetHelpCenterIdentity()
  analytics.reset()
  $log('Login page session cleared')
}

export const resendEmailVerificationMail = async (
  email: string,
  password: string,
): Promise<ResendVerificationResult> => {
  const auth = getAuth()
  const normalizedEmail = normalizeEmailInput(email)
  try {
    // Temporarily sign in if there is no authenticated session.
    const userCredential = await signInWithEmailAndPassword(
      auth,
      normalizedEmail,
      password,
    )
    const user = userCredential.user
    if (!user.emailVerified) {
      await sendEmailVerification(user)
      $log('Verification Mail has been sent to:', user.email)
      return { status: 'sent' }
    }
    else {
      $log('E-Mail is already verified:', user.email)
      return { status: 'already-verified' }
    }
  }
  catch (error: unknown) {
    const errorCode = getErrorCode(error)

    if (errorCode === 'auth/too-many-requests') {
      return { status: 'rate-limited' }
    }

    if (errorCode === 'auth/multi-factor-auth-required') {
      return { status: 'failed', code: 'mfa-required' }
    }

    if (isInvalidLoginCredentialError(error)) {
      return { status: 'invalid-credentials' }
    }

    $reportError('Fehler beim Senden der Verifizierungsmail', error)
    return { status: 'failed', code: errorCode }
  }
  finally {
    await auth.signOut()
  }
}

export const sendEmailResetMail = () => {
  const auth = getAuth()
  const user = auth.currentUser
  if (!user) {
    $logError('No authenticated user found.')
    return
  }
  sendEmailVerification(user)
    .then(() => {
      $log('Verification Mail has been sent to: ', user.email)
    })
    .catch((error) => {
      $reportError('Error while trying to update the email', error)
    })
}

export const changeUserEmail = async (newEmail: string) => {
  const auth = getAuth()
  if (!auth.currentUser) {
    $logError('No authenticated user found.')
    return
  }
  await verifyBeforeUpdateEmail(auth.currentUser, normalizeEmailInput(newEmail))
}

export const requestNewPasswordForMail = (email: string) => {
  const auth = getAuth()
  const normalizedEmail = normalizeEmailInput(email)

  sendPasswordResetEmail(auth, normalizedEmail)
    .then(() => {
      $log('Password-reset Mail has been sent')
    })
    .catch((error: unknown) => {
      $log(
        'There has been an error while trying to send the password-reset mail:',
      )
      $log(errorMessage(error))
    })
}

/**
 * Canonical Firebase auth surface for the app.
 * `plugins/firebaseAuth.ts` initializes Firebase once and calls `initUser()`.
 * Backend profile/customer state belongs in `stores/useUserStore.ts`, not here.
 */
export function useFirebase() {
  return {
    firebaseUser,
    isUserLoaded,
    getToken,
    signInUser,
    signOutUser,
    prepareLoginPageSession,
    createUser,
    initUser,
    clearCachedAuthToken,
    syncCachedAuthToken,
    signInWithEmailMfaToken,
    pauseInactivityTimer,
    resumeInactivityTimer,
    clearLocalImpersonationSession,
  }
}

export { firebaseUser, isUserLoaded }
